// EAA-1667: Small, safe localStorage primitives, moved out of web-content's
// localUserPreferences util so any app can persist typed user preferences without
// re-implementing the try/catch + validation dance. SSR-safe (no-op when there's no
// `window`) and quota/private-mode-safe (writes swallow errors). Apps layer their own
// keys + validators on top; the concrete keys stay app-side.

function hasStorage(): boolean {
    return typeof window !== 'undefined' && typeof window.localStorage !== 'undefined';
}

/**
 * Read + JSON-parse `key`, returning `fallback` when absent, unparseable, or failing
 * `validate`. Never throws.
 */
export function readJson<T>(key: string, validate: (value: unknown) => value is T, fallback: T): T {
    if (!hasStorage()) return fallback;
    try {
        const raw = window.localStorage.getItem(key);
        if (raw == null) return fallback;
        const parsed = JSON.parse(raw) as unknown;
        return validate(parsed) ? parsed : fallback;
    } catch {
        return fallback;
    }
}

/** JSON-stringify + write `value` at `key`. Swallows quota / private-mode errors. */
export function writeJson(key: string, value: unknown): void {
    if (!hasStorage()) return;
    try {
        window.localStorage.setItem(key, JSON.stringify(value));
    } catch {
        /* quota or private mode */
    }
}

/** Read a raw string at `key`, or `null` when absent/blank. Never throws. */
export function readString(key: string): string | null {
    if (!hasStorage()) return null;
    try {
        const raw = window.localStorage.getItem(key);
        if (raw == null || raw.trim() === '') return null;
        return raw;
    } catch {
        return null;
    }
}

/** Write a raw string at `key`. Swallows quota / private-mode errors. */
export function writeString(key: string, value: string): void {
    if (!hasStorage()) return;
    try {
        window.localStorage.setItem(key, value);
    } catch {
        /* quota or private mode */
    }
}

/** Remove `key`. Swallows errors. Never throws. */
export function removeKey(key: string): void {
    if (!hasStorage()) return;
    try {
        window.localStorage.removeItem(key);
    } catch {
        /* quota or private mode */
    }
}
